import { DestroyRef, effect, inject, signal, type Signal } from '@angular/core';

import { useNetworkStatus } from './use-network-status';

export type ConnectionBannerStatus = 'offline' | 'restored';

/** How long the "connection restored" message stays visible before hiding. */
export const RESTORED_BANNER_DURATION_MS = 3000;

export function useConnectionStatusBanner(online: Signal<boolean> = useNetworkStatus().online): {
    status: Signal<ConnectionBannerStatus | null>;
} {
    const status = signal<ConnectionBannerStatus | null>(online() ? null : 'offline');

    let restoredTimer: ReturnType<typeof setTimeout> | undefined;
    const clearRestoredTimer = () => {
        clearTimeout(restoredTimer);
        restoredTimer = undefined;
    };

    let wasOffline = !online();

    effect(() => {
        if (!online()) {
            clearRestoredTimer();
            wasOffline = true;
            status.set('offline');
            return;
        }

        if (!wasOffline) return;

        wasOffline = false;
        status.set('restored');
        clearRestoredTimer();
        restoredTimer = setTimeout(() => status.set(null), RESTORED_BANNER_DURATION_MS);
    });

    inject(DestroyRef).onDestroy(clearRestoredTimer);

    return { status: status.asReadonly() };
}
